'use client'

import { asyncWithLDProvider } from 'launchdarkly-react-client-sdk'
import React, { useEffect, useState, ReactNode, ComponentType } from 'react'
import { Box } from '@mui/material'
import { useSession } from 'next-auth/react'
import { getEnvVariable } from '@/app/service/getenvService'

interface LDProviderWrapperProps {
	children: ReactNode
}

/**
 * A wrapper component for integrating LaunchDarkly with a React application.
 * This component fetches the LaunchDarkly client ID from an API route and initializes
 * the LaunchDarkly provider with the fetched client ID and context values from the session.
 *
 * @param {Object} props - The component props.
 * @param {ReactNode} props.children - The child components to be rendered within the LaunchDarkly provider.
 *
 * @returns {JSX.Element | null} The LaunchDarkly provider component wrapping the children, or null if the provider is not yet initialized.
 */
function LDProviderWrapper({ children }: { readonly children: ReactNode }) {
	const [ldProvider, setLdProvider] =
		useState<ComponentType<LDProviderWrapperProps> | null>(null)
	const { data: session, status } = useSession()
	const [isLoading, setIsLoading] = useState(true)

	useEffect(() => {
		const setupLD = async () => {
			setIsLoading(true)
			try {
				// Fetch the client ID from the API route
				const clientId = await getEnvVariable('NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_ID')
				if (!clientId) {
					console.error('LaunchDarkly client ID is not available')
					return
				}

				// Initialize LaunchDarkly provider with the fetched client ID and context values from the session
				const provider = await asyncWithLDProvider({
					clientSideID: clientId,
					context: {
						kind: 'user',
						key: session?.user?.id ?? 'global-key',
						name: session?.user?.name ?? 'anonymous',
						email: session?.user?.email ?? '',
						companyId: session?.user?.companyId ?? '',
					},
				})
				setLdProvider(() => provider)
				setIsLoading(false)
			} catch (error) {
				setIsLoading(false)
				console.error('Failed to fetch LaunchDarkly client ID:', error)
			}
		}
		setupLD()
	}, [status])

	// added to fix sonarqube issue reported. Components must start with a capital letter. useState variables must start with a lowercase letter.
	const LdProviderAlias = ldProvider
	if (isLoading) {
		return (
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'center',
					alignItems: 'center',
					height: '100vh',
				}}
				data-testid="ld-loading"
			></Box>
		)
	}

	if (LdProviderAlias) {
		return <LdProviderAlias>{children}</LdProviderAlias>
	}

	// LD failed to initialize — render children without the LD provider
	// so the app can still function (feature flags default to false).
	// This is critical for users with no permissions: the route guard
	// must still render to show the unauthorized error view.
	return <>{children}</>

}

export default LDProviderWrapper
